import { weekFileName } from '../../lib/weekFileName';
import { ListTotalsBar, sumByCurrency } from '../ui/list-totals-bar';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import React, { useState, useEffect, useCallback } from 'react';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import RichTextNotes from '../RichTextNotes';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Plus,
  FileText,
  Download,
  CreditCard,
  AlertTriangle,
  Search,
  Eye,
  Trash2,
  X,
  Clock,
  PenLine,
  BookMarked,
  Send,
  RotateCcw,
  Cloud,
  Save,
} from 'lucide-react';
import FloatingActions from '../FloatingActions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import EmployeeInvoicesTab from './EmployeeInvoicesTab';
import RecurringTemplatesTab from './RecurringTemplatesTab';
import SendInvoiceEmailDialog from './SendInvoiceEmailDialog';
import KSeFTab from './KSeFTab';
import { SortableHeader, useSort, ResetViewButton } from '../ui/SortableHeader';
import { usePersistedState } from '../ui/usePersistedState';
import usePermissions from '../../hooks/usePermissions';
import useDeepShortcut from '../../hooks/useDeepShortcut';
import useSubTabShortcut from '../../hooks/useSubTabShortcut';
import useRememberedInputs from '../../hooks/useRememberedInputs';
import { DateText } from '../ui/week-badge';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  sent: 'bg-purple-100 text-purple-800',
  paid: 'bg-green-100 text-green-800',
  partially_paid: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-300 text-gray-600',
};

// English fallbacks only — useStatusLabel() prefers the invoices.status.* bundle.
const statusLabels = {
  draft: 'Draft',
  issued: 'Issued',
  sent: 'Sent',
  paid: 'Paid',
  partially_paid: 'Partially Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

/**
 * Translate an invoice status key. Falls back to the English
 * ``statusLabels`` map for values that don't yet have a
 * translation (e.g. legacy ``sent`` before it moves into the
 * bundle).
 */
const useStatusLabel = () => {
  const { t } = useTranslation();
  return (statusKey) => t(`invoices.status.${statusKey}`, {
    defaultValue: statusLabels[statusKey] || statusKey,
  });
};

const fmtCurrency = (amount, currency = 'EUR') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(amount);
};

// Dates in the invoice tables/dialogs carry their ISO week badge.
const fmtDate = (dateStr) => <DateText value={dateStr} empty="-" />;

const InvoicesTab = ({ projects, customers, currentUser }) => {
  const isAdmin = currentUser?.role === 'admin';
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState('customer');
  useSubTabShortcut({
    tab: 'invoices', ctxKey: 'invoicesSubTab', value: subTab, setValue: setSubTab,
    labels: { customer: 'invoices.subtabs.customer', employee: 'invoices.subtabs.employee',
      recurring: 'invoices.subtabs.recurring', ksef: 'invoices.subtabs.ksef' },
    allowed: ['customer', 'employee', 'recurring', ...(isAdmin ? ['ksef'] : [])],
  });

  return (
    <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
      <TabsList className="flex flex-wrap h-auto gap-1 p-1">
        <TabsTrigger value="customer" data-testid="customer-invoices-subtab">{t('invoices.subtabs.customer')}</TabsTrigger>
        <TabsTrigger value="employee" data-testid="employee-invoices-subtab">{t('invoices.subtabs.employee')}</TabsTrigger>
        <TabsTrigger value="recurring" data-testid="recurring-templates-subtab">{t('invoices.subtabs.recurring')}</TabsTrigger>
        {isAdmin && (
          <TabsTrigger value="ksef" data-testid="ksef-subtab" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            {t('invoices.subtabs.ksef')}
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="customer">
        <CustomerInvoices projects={projects} customers={customers} currentUser={currentUser} />
      </TabsContent>
      <TabsContent value="employee">
        <EmployeeInvoicesTab currentUser={currentUser} />
      </TabsContent>
      <TabsContent value="recurring">
        <RecurringTemplatesTab currentUser={currentUser} customers={customers} />
      </TabsContent>
      {isAdmin && (
        <TabsContent value="ksef">
          <KSeFTab user={currentUser} />
        </TabsContent>
      )}
    </Tabs>
  );
};

const CustomerInvoices = ({ projects, customers, currentUser }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canViewAlerts = can('invoice_alerts.view');
  const statusLabel = useStatusLabel();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfInvoice, setPdfInvoice] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  // Dashboard shortcuts: pin the invoice the user has open.
  useDeepShortcut({
    active: showViewDialog && !!selectedInvoice,
    tab: 'invoices',
    context: { focusInvoiceId: selectedInvoice?.id },
    label: selectedInvoice?.invoice_number,
    subtitle: t('invoiceDlg.invoices'),
    icon: 'invoices',
    perms: ['invoices.view'],
    priority: 3,
    focusKey: 'focusInvoiceId',
    onFocus: (id) => {
      const found = invoices.find((i) => i.id === id);
      if (found) { setSelectedInvoice(found); setShowViewDialog(true); }
    },
  });
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [uninvoicedHours, setUninvoicedHours] = useState([]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = usePersistedState('customer-invoices.searchTerm', '');
  // Phase 4 — correction filter: 'all' | 'originals' | 'corrections' | 'superseded'
  const [correctionFilter, setCorrectionFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState(null);
  useRememberedInputs('customer-invoices-filters', {
    statusFilter: [statusFilter, setStatusFilter], customerFilter: [customerFilter, setCustomerFilter],
    correctionFilter: [correctionFilter, setCorrectionFilter], weekFilter: [weekFilter, setWeekFilter],
  });
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('issue_date', 'desc', 'customer-invoices');
  const handleSort = (key) => {
    toggleSort(key, key === 'issue_date' || key === 'due_date' ? 'desc' : 'asc');
  };

  const isViewCustomized = sortKey !== 'issue_date'
    || sortDir !== 'desc'
    || statusFilter !== 'all'
    || customerFilter !== 'all'
    || correctionFilter !== 'all'
    || !!weekFilter
    || searchTerm !== '';

  const handleResetView = () => {
    resetSort();
    setStatusFilter('all');
    setCustomerFilter('all');
    setCorrectionFilter('all');
    setWeekFilter(null);
    setSearchTerm('');
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(t('invoices.toasts.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUninvoicedHours = useCallback(async () => {
    // Admin-only signal — skip the call entirely without the permission
    // so non-admins never see a 403 in the console.
    if (!canViewAlerts) { setUninvoicedHours([]); return; }
    try {
      const response = await axios.get(`${API}/api/invoices/uninvoiced-hours`);
      setUninvoicedHours(response.data);
    } catch (error) {
      console.error('Error fetching uninvoiced hours:', error);
    }
  }, [canViewAlerts]);

  useEffect(() => {
    fetchInvoices();
    fetchUninvoicedHours();
  }, [fetchInvoices, fetchUninvoicedHours]);

  const handleIssueInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/api/invoices/${invoiceId}/issue`);
      toast.success(t('invoices.toasts.issued'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.toasts.issueError'));
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm(t('invoiceDlg.areYouSureYouWantTo'))) return;
    try {
      await axios.delete(`${API}/api/invoices/${invoiceId}`);
      toast.success(t('invoices.toasts.deleted'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.toasts.deleteError'));
    }
  };

  const handleRevertToDraft = async (invoiceId) => {
    if (!window.confirm(t('invoiceDlg.revertThisInvoiceToDraftStatus'))) return;
    try {
      await axios.post(`${API}/api/invoices/${invoiceId}/revert-draft`);
      toast.success(t('invoices.toasts.reverted'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.toasts.revertError'));
    }
  };

  const handleCreateCorrection = async (originalInvoice) => {
    const reason = window.prompt(
      `Create a correction (faktura korygująca) for ${originalInvoice.invoice_number}?\n\n` +
      `Briefly state the reason for correction (will appear on the FA(3) XML as PrzyczynaKorekty):`,
      'Correction of invoice ' + originalInvoice.invoice_number,
    );
    if (reason === null) return;
    try {
      const r = await axios.post(
        `${API}/api/invoices/${originalInvoice.id}/create-correction`,
        { correction_reason: reason || 'Korekta faktury', correction_type: 2 },
      );
      toast.success(t('invoices.toasts.correctionCreated', { number: r.data.invoice_number }));
      fetchInvoices();
      // Open the freshly-created draft in the edit dialog
      setSelectedInvoice(r.data);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.toasts.correctionError'));
    }
  };

  const getCustomerName = (customerId) => customers.find(c => c.id === customerId)?.name || 'Unknown';
  const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || '-';

  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
    if (customerFilter !== 'all' && invoice.customer_id !== customerFilter) return false;
    if (correctionFilter === 'originals' && invoice.is_correction) return false;
    if (correctionFilter === 'corrections' && !invoice.is_correction) return false;
    if (correctionFilter === 'superseded' && !invoice.corrected_by_invoice_id) return false;
    if (!matchesWeek(weekFilter, invoice.issue_date)) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!invoice.invoice_number?.toLowerCase().includes(s) &&
          !getCustomerName(invoice.customer_id).toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const sortedInvoices = [...filteredInvoices].sort(comparator((inv, key) => {
    switch (key) {
      case 'invoice_number':
        return inv.invoice_number || '';
      case 'customer':
        return getCustomerName(inv.customer_id).toLowerCase();
      case 'project':
        return getProjectName(inv.project_id).toLowerCase();
      case 'issue_date':
        return inv.issue_date ? new Date(inv.issue_date).getTime() : 0;
      case 'due_date':
        return inv.due_date ? new Date(inv.due_date).getTime() : 0;
      case 'amount':
        // Compare in PLN when available for fair cross-currency ordering.
        return inv.total_gross_pln ?? inv.total_gross ?? 0;
      case 'status':
        return inv.status || '';
      default:
        return '';
    }
  }));

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    issued: invoices.filter(i => ['issued', 'sent'].includes(i.status)).length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    outstanding: invoices
      .filter(i => ['issued', 'sent', 'partially_paid'].includes(i.status))
      .reduce((sum, i) => sum + (i.amount_remaining || 0), 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800" data-testid="invoices-heading">{t('invoices.title')}</h2>
          <p className="text-slate-500">{t('invoices.subtitle')}</p>
        </div>
        <Button onClick={() => { setSelectedInvoice(null); setShowCreateDialog(true); }} className="bg-[#AA0000] hover:bg-[#880000] shrink-0" data-testid="new-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('invoices.newInvoice')}
        </Button>
      </div>

      {canViewAlerts && uninvoicedHours.length > 0 && (
        <Card className="border-orange-200 bg-orange-50" data-testid="uninvoiced-warning">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">{t('invoices.uninvoicedHours.title')}</h4>
                <p className="text-sm text-orange-600 mt-1">
                  {t('invoices.uninvoicedHours.summary', { count: uninvoicedHours.length })}
                </p>
                <div className="mt-2 space-y-1">
                  {uninvoicedHours.slice(0, 3).map(item => (
                    <div key={item.project_id} className="text-sm text-orange-700">
                      <strong>{item.project_name}</strong>: {item.uninvoiced_hours} {t('invoices.uninvoicedHours.hours')}
                    </div>
                  ))}
                  {uninvoicedHours.length > 3 && (
                    <div className="text-sm text-orange-600">{t('invoices.uninvoicedHours.more', { count: uninvoicedHours.length - 3 })}</div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: t('invoices.stats.total'), value: stats.total, color: 'text-slate-800' },
          { label: t('invoices.stats.drafts'), value: stats.draft, color: 'text-gray-600' },
          { label: t('invoices.stats.issued'), value: stats.issued, color: 'text-blue-600' },
          { label: t('invoices.stats.paid'), value: stats.paid, color: 'text-green-600' },
          { label: t('invoices.stats.overdue'), value: stats.overdue, color: 'text-red-600' },
          { label: t('invoices.stats.outstanding'), value: fmtCurrency(stats.outstanding, 'EUR'), color: 'text-[#AA0000]' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 sm:p-4">
              <div className={`text-xl sm:text-2xl font-bold break-words ${s.color}`}>{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-500 break-words">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('invoices.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="invoice-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder={t('invoices.filters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('invoices.filters.allStatuses')}</SelectItem>
                {Object.keys(statusLabels).map((k) => (
                  <SelectItem key={k} value={k}>{statusLabel(k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={correctionFilter} onValueChange={setCorrectionFilter}>
              <SelectTrigger className="w-[150px]" data-testid="correction-filter" title={t('invoiceDlg.showOnlyOriginalsCorrectionsOrSuperseded')}>
                <SelectValue placeholder={t('invoices.filters.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('invoices.filters.allInvoices')}</SelectItem>
                <SelectItem value="originals">{t('invoices.filters.originals')}</SelectItem>
                <SelectItem value="corrections">{t('invoices.filters.corrections')}</SelectItem>
                <SelectItem value="superseded">{t('invoices.filters.superseded')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-[200px]" data-testid="customer-filter">
                <SelectValue placeholder={t('invoices.filters.customer')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('invoices.filters.allCustomers')}</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="invoice-week-filter" />
            {isViewCustomized && (
              <ResetViewButton
                show
                onReset={handleResetView}
                testId="reset-view-customer-invoices"
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.title')}</CardTitle>
          <CardDescription>{t('invoices.foundCount', { count: filteredInvoices.length })}</CardDescription>
        </CardHeader>
        <CardContent>
          <ListTotalsBar
            count={filteredInvoices.length}
            week={weekFilter}
            groups={[
              { key: 'net', label: t('totals.net'), values: sumByCurrency(filteredInvoices, (r) => r.currency, (r) => r.total_net) },
              { key: 'gross', label: t('totals.gross'), values: sumByCurrency(filteredInvoices, (r) => r.currency, (r) => r.total_gross) },
            ]}
            testId="invoice-totals"
            copyable
            className="-mx-6 -mt-2 mb-3"
          />
          {loading ? (
            <div className="text-center py-8 text-slate-500">{t('common.loading')}</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="no-invoices">{t('invoices.empty')}</div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader column="invoice_number" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.number')}</SortableHeader>
                  <SortableHeader column="customer" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.customer')}</SortableHeader>
                  <SortableHeader column="project" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.project')}</SortableHeader>
                  <SortableHeader column="issue_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.issueDate')}</SortableHeader>
                  <SortableHeader column="due_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.dueDate')}</SortableHeader>
                  <SortableHeader column="amount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">{t('invoices.columns.amount')}</SortableHeader>
                  <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('invoices.columns.status')}</SortableHeader>
                  <TableHead className="text-right">{t('invoices.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedInvoices.map(invoice => {
                  // Look up the corrected-by invoice for traceability badge
                  const corrBy = invoice.corrected_by_invoice_id
                    ? invoices.find(x => x.id === invoice.corrected_by_invoice_id)
                    : null;
                  return (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-medium">
                      <div>{invoice.invoice_number}</div>
                      {invoice.is_correction && invoice.corrects_invoice_number && (
                        <div className="text-[10px] text-blue-700 leading-tight" title={t('invoiceDlg.thisIsACorrectionFakturaKoryguj')}>
                          ← Corrects <span className="font-mono">{invoice.corrects_invoice_number}</span>
                        </div>
                      )}
                      {corrBy && (
                        <div className="text-[10px] text-amber-700 leading-tight" title={t('invoiceDlg.supersededByCorrection')}>
                          → Corrected by <span className="font-mono">{corrBy.invoice_number}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getCustomerName(invoice.customer_id)}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{getProjectName(invoice.project_id)}</TableCell>
                    <TableCell>{fmtDate(invoice.issue_date)}</TableCell>
                    <TableCell>{fmtDate(invoice.due_date)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-0.5 whitespace-nowrap">
                        {fmtCurrency(invoice.total_gross, invoice.currency)}
                        <CopyNumberButton value={invoice.total_gross} size="sm" testId={`copy-invoice-gross-${invoice.id}`} />
                      </div>
                      {invoice.currency !== 'PLN' && invoice.total_gross_pln && (
                        <div className="flex items-center justify-end gap-0.5 whitespace-nowrap text-xs text-slate-500">
                          ({fmtCurrency(invoice.total_gross_pln, 'PLN')})
                          <CopyNumberButton value={invoice.total_gross_pln} size="sm" testId={`copy-invoice-gross-pln-${invoice.id}`} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[invoice.status]}>{statusLabel(invoice.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title={t('invoiceDlg.view')} data-testid={`view-invoice-${invoice.id}`}
                          onClick={() => { setSelectedInvoice(invoice); setShowViewDialog(true); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t('invoiceDlg.downloadPdf')} data-testid={`download-invoice-${invoice.id}`}
                          onClick={() => { setPdfInvoice(invoice); setShowPdfDialog(true); }}>
                          <Download className="h-4 w-4" />
                        </Button>
                        {invoice.status === 'draft' && (
                          <Button variant="ghost" size="icon" title={t('invoiceDlg.issueInvoice')} data-testid={`issue-invoice-${invoice.id}`}
                            onClick={() => handleIssueInvoice(invoice.id)}>
                            <FileText className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {invoice.status !== 'draft' && (
                          <Button variant="ghost" size="icon" title={t('invoiceDlg.revertToDraft')} data-testid={`revert-invoice-${invoice.id}`}
                            onClick={() => handleRevertToDraft(invoice.id)}>
                            <RotateCcw className="h-4 w-4 text-orange-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title={t('common.edit')} data-testid={`edit-invoice-${invoice.id}`}
                          onClick={() => { setSelectedInvoice(invoice); setShowCreateDialog(true); }}>
                          <PenLine className="h-4 w-4 text-amber-600" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t('common.delete')} data-testid={`delete-invoice-${invoice.id}`}
                          onClick={() => handleDeleteInvoice(invoice.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                        {['issued', 'sent', 'partially_paid', 'paid'].includes(invoice.status)
                          && !invoice.is_correction
                          && !invoice.corrected_by_invoice_id && (
                          <Button variant="ghost" size="icon" title={t('invoiceDlg.createCorrectionFakturaKorygujCa')}
                            data-testid={`correct-invoice-${invoice.id}`}
                            onClick={() => handleCreateCorrection(invoice)}>
                            <RotateCcw className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {invoice.is_correction && (
                          <span className="inline-flex items-center px-1.5 h-6 rounded text-[10px] font-medium bg-blue-100 text-blue-800"
                            title={`Correction of ${invoice.corrects_invoice_number || ''}`}>
                            {t('auto.misc2.kor')}
                          </span>
                        )}
                        {['issued', 'sent', 'partially_paid'].includes(invoice.status) && (
                          <>
                            <Button variant="ghost" size="icon" title={t('invoiceDlg.sendEmail')} data-testid={`send-invoice-${invoice.id}`}
                              onClick={() => { setEmailInvoice(invoice); setShowEmailDialog(true); }}>
                              <Send className="h-4 w-4 text-purple-600" />
                            </Button>
                            <Button variant="ghost" size="icon" title={t('invoiceDlg.recordPayment')} data-testid={`pay-invoice-${invoice.id}`}
                              onClick={() => { setSelectedInvoice(invoice); setShowPaymentDialog(true); }}>
                              <CreditCard className="h-4 w-4 text-green-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateInvoiceDialog
        open={showCreateDialog}
        onOpenChange={(v) => { setShowCreateDialog(v); if (!v) setSelectedInvoice(null); }}
        projects={projects}
        customers={customers}
        editInvoice={selectedInvoice || null}
        currentUser={currentUser}
        onSuccess={() => { fetchInvoices(); fetchUninvoicedHours(); setSelectedInvoice(null); }}
      />

      <PaymentDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        invoice={selectedInvoice}
        onSuccess={fetchInvoices}
      />

      <ViewInvoiceDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        invoice={selectedInvoice}
        getCustomerName={getCustomerName}
        getProjectName={getProjectName}
      />

      <PdfDownloadDialog
        open={showPdfDialog}
        onOpenChange={setShowPdfDialog}
        invoice={pdfInvoice}
      />

      <SendInvoiceEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        invoice={emailInvoice}
        customers={customers}
        onSuccess={fetchInvoices}
      />
    </div>
  );
};

// ==================== Create / Edit Invoice Dialog ====================
const CreateInvoiceDialog = ({ open, onOpenChange, projects, customers, editInvoice, currentUser, onSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('timesheet'); // 'timesheet' or 'manual'
  const [selectedProject, setSelectedProject] = useState('');
  // Timesheet mode can bill SEVERAL projects on one invoice — each
  // project contributes its own hours line (plus its expense lines).
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [savedItems, setSavedItems] = useState([]);
  const [showSavedPicker, setShowSavedPicker] = useState(false);
  const [periodEnd, setPeriodEnd] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [companyBanks, setCompanyBanks] = useState({});

  const [invoiceData, setInvoiceData] = useState({
    currency: 'EUR',
    reverse_charge: true,
    vat_rate: 0,
    notes: '',
    reverse_charge_text: '„ODWROTNE OBCIĄŻENIE" PODATEK VAT OPŁACA NABYWCA USŁUGI, Opodatkowanie w kraju położenia nieruchomości (art. 28e ustawy o VAT i art. 47 dyrektywy 2006/112/WE);\n"REVERSE CHARGE" VAT IS PAID BY THE BUYER OF THE SERVICE, Taxation in the country where the real estate is located (Article 28e of the VAT Act and Article 47 of Directive 2006/112/EC)',
    supplier_number: '',
    location: '',
    contact_info: '',
    line_items: [],
    issue_date: '',
    sale_date: '',
    due_date: '',
    bank_account: '',
    issued_by_name: '',
    invoice_number: '',
    // ---- Per-invoice party overrides (Phase 2) ----
    seller_name: '',
    seller_address: '',
    seller_tax_id: '',
    seller_country_code: '',
    buyer_name: '',
    buyer_address: '',
    buyer_tax_id: '',
    buyer_vat_number: '',
    buyer_country_code: '',
    // ---- Payment method + already-paid flag (Phase 5) ----
    payment_method: 'bank_transfer',
    is_paid: false,
    paid_date: '',
  });
  // Show/hide the party-override panel (Phase 2)
  const [showPartyOverride, setShowPartyOverride] = useState(false);
  // Company profile snapshot for placeholders
  const [companyProfile, setCompanyProfile] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Fetch saved invoice items
    axios.get(`${API}/api/settings/saved-invoice-items`).then(r => setSavedItems(r.data)).catch(() => {});
    // Fetch company bank accounts
    axios.get(`${API}/api/company/bank-accounts`).then(r => {
      setCompanyBanks(r.data.accounts || {});
    }).catch(() => {});
    // Fetch company profile (for seller-override placeholders)
    axios.get(`${API}/api/company/profile`).then(r => setCompanyProfile(r.data)).catch(() => {});
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const firstDay = format(new Date(y, m, 1), 'yyyy-MM-dd');
    const lastDay = format(new Date(y, m + 1, 0), 'yyyy-MM-dd');

    if (editInvoice) {
      // Load existing draft for editing
      setStep(2);
      setMode(editInvoice.project_id ? 'timesheet' : 'manual');
      setSelectedProject(editInvoice.project_id || '');
      setSelectedProjects(
        (editInvoice.project_ids && editInvoice.project_ids.length)
          ? editInvoice.project_ids
          : (editInvoice.project_id ? [editInvoice.project_id] : [])
      );
      setSelectedCustomer(editInvoice.customer_id || '');
      setInvoiceData({
        currency: editInvoice.currency || 'EUR',
        reverse_charge: editInvoice.reverse_charge ?? true,
        vat_rate: editInvoice.vat_rate || 0,
        notes: editInvoice.notes || '',
        reverse_charge_text: editInvoice.reverse_charge_text || '',
        supplier_number: editInvoice.supplier_number || '',
        location: editInvoice.location || '',
        contact_info: editInvoice.contact_info || '',
        line_items: editInvoice.line_items || [],
        issue_date: editInvoice.issue_date ? editInvoice.issue_date.substring(0, 10) : lastDay,
        sale_date: editInvoice.sale_date ? editInvoice.sale_date.substring(0, 10) : lastDay,
        due_date: editInvoice.due_date ? editInvoice.due_date.substring(0, 10) : '',
        issued_by_name: editInvoice.issued_by_name || '',
        invoice_number: editInvoice.invoice_number || '',
        // Phase 2 overrides
        seller_name: editInvoice.seller_name || '',
        seller_address: editInvoice.seller_address || '',
        seller_tax_id: editInvoice.seller_tax_id || '',
        seller_country_code: editInvoice.seller_country_code || '',
        buyer_name: editInvoice.buyer_name || '',
        buyer_address: editInvoice.buyer_address || '',
        buyer_tax_id: editInvoice.buyer_tax_id || '',
        buyer_vat_number: editInvoice.buyer_vat_number || '',
        buyer_country_code: editInvoice.buyer_country_code || '',
        payment_method: editInvoice.payment_method || 'bank_transfer',
        is_paid: !!editInvoice.is_paid,
        paid_date: editInvoice.paid_date ? editInvoice.paid_date.substring(0, 10) : '',
      });
      // Auto-expand override panel if any override is set on the loaded invoice
      setShowPartyOverride(!!(
        editInvoice.seller_name || editInvoice.seller_address || editInvoice.seller_tax_id
        || editInvoice.buyer_name || editInvoice.buyer_address || editInvoice.buyer_tax_id
        || editInvoice.buyer_vat_number
      ));
      setDraft({
        project: projects.find(p => p.id === editInvoice.project_id) || null,
        projects: (
          (editInvoice.project_ids && editInvoice.project_ids.length)
            ? editInvoice.project_ids
            : (editInvoice.project_id ? [editInvoice.project_id] : [])
        ).map(id => projects.find(p => p.id === id)).filter(Boolean),
        customer: customers.find(c => c.id === editInvoice.customer_id) || null,
        exchange_rate: editInvoice.exchange_rate ? {
          rate: editInvoice.exchange_rate,
          date: editInvoice.exchange_rate_date,
          table: editInvoice.exchange_rate_table,
          currency: editInvoice.currency,
        } : null,
      });
    } else {
      setStep(1);
      setMode('timesheet');
      setDraft(null);
      setSelectedProject('');
      setSelectedProjects([]);
      setSelectedCustomer('');
      setPeriodStart(firstDay);
      setPeriodEnd(lastDay);
      setInvoiceData({
        currency: 'EUR',
        reverse_charge: true,
        vat_rate: 0,
        notes: '',
        reverse_charge_text: '„ODWROTNE OBCIĄŻENIE" PODATEK VAT OPŁACA NABYWCA USŁUGI, Opodatkowanie w kraju położenia nieruchomości (art. 28e ustawy o VAT i art. 47 dyrektywy 2006/112/WE);\n"REVERSE CHARGE" VAT IS PAID BY THE BUYER OF THE SERVICE, Taxation in the country where the real estate is located (Article 28e of the VAT Act and Article 47 of Directive 2006/112/EC)',
        supplier_number: '',
        location: '',
        contact_info: '',
        line_items: [],
        issue_date: lastDay,
        sale_date: lastDay,
        due_date: '',
        issued_by_name: currentUser?.full_name || currentUser?.username || '',
        invoice_number: '',
        seller_name: '', seller_address: '', seller_tax_id: '', seller_country_code: '',
        buyer_name: '', buyer_address: '', buyer_tax_id: '', buyer_vat_number: '', buyer_country_code: '',
        payment_method: 'bank_transfer',
        is_paid: false,
        paid_date: '',
      });
      setShowPartyOverride(false);
    }
  }, [open, editInvoice, projects, customers]);

  const toggleProject = (projectId) => {
    setSelectedProjects(prev => (
      prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
    ));
  };

  const handleLoadDraft = async () => {
    if (!selectedProjects.length || !periodStart || !periodEnd) {
      toast.error(t('invoices.toasts.needProjectPeriod'));
      return;
    }
    // One invoice = one buyer, so every picked project must belong to
    // the same customer.
    const customerIds = [...new Set(
      selectedProjects.map(id => projects.find(p => p.id === id)?.customer_id).filter(Boolean)
    )];
    if (customerIds.length > 1) {
      toast.error(t('invoices.toasts.projectsDifferentCustomers'));
      return;
    }
    setLoading(true);
    try {
      const responses = await Promise.all(selectedProjects.map(pid => (
        axios.get(`${API}/api/invoices/draft/${pid}?start_date=${periodStart}&end_date=${periodEnd}`)
          .then(r => ({ projectId: pid, data: r.data }))
      )));
      const first = responses[0].data;
      // Merge every project's line items, tagging each with its project
      // so the invoice keeps the link after saving.
      const mergedItems = [];
      const expenseNotes = [];
      responses.forEach(({ projectId, data }) => {
        (data.line_items || []).forEach(li => mergedItems.push({
          ...li,
          project_id: projectId,
          project_name: data.project?.name || '',
        }));
        if (data.expense_notes) expenseNotes.push(data.expense_notes);
      });
      const draftProjects = responses.map(r => r.data.project).filter(Boolean);
      const d = { ...first, projects: draftProjects };
      setDraft(d);
      const payTerms = d.customer?.payment_terms || 14;
      const endDate = new Date(periodEnd);
      const dueDate = format(new Date(endDate.getTime() + payTerms * 86400000), 'yyyy-MM-dd');

      const projectCurrency = d.project?.currency || 'EUR';
      const bankForCurrency = companyBanks[projectCurrency]?.iban || '';
      const locations = [...new Set(draftProjects.map(p => (
        [p?.site_address, p?.site_city, p?.site_postal_code, p?.site_country].filter(Boolean).join(', ')
      )).filter(Boolean))];
      setInvoiceData(prev => ({
        ...prev,
        currency: projectCurrency,
        reverse_charge: true,
        reverse_charge_text: d.reverse_charge_text || prev.reverse_charge_text,
        line_items: mergedItems,
        issue_date: periodEnd,
        sale_date: periodEnd,
        due_date: dueDate,
        bank_account: bankForCurrency,
        location: locations.join(' | ') || prev.location,
        notes: expenseNotes.length
          ? (prev.notes ? prev.notes + '\n' + expenseNotes.join('\n') : expenseNotes.join('\n'))
          : prev.notes,
        // Phase 2: prefill buyer overrides from draft customer
        buyer_name: prev.buyer_name || d.customer?.name || '',
        buyer_address: prev.buyer_address || d.customer?.invoice_address || d.customer?.address || '',
        buyer_tax_id: prev.buyer_tax_id || d.customer?.tax_id || d.customer?.vat_number || '',
        buyer_vat_number: prev.buyer_vat_number || d.customer?.vat_number || '',
        buyer_country_code: prev.buyer_country_code || (d.customer?.country || 'PL').slice(0, 2).toUpperCase(),
      }));
      setSelectedProject(selectedProjects[0]);
      setSelectedCustomer(d.customer?.id || '');
      setStep(2);
    } catch (error) {
      toast.error(t('invoices.toasts.loadDraftError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartManual = () => {
    if (!selectedCustomer) {
      toast.error(t('invoices.toasts.needCustomer'));
      return;
    }
    const customer = customers.find(c => c.id === selectedCustomer);
    const project = selectedProject ? projects.find(p => p.id === selectedProject) : null;
    const payTerms = customer?.payment_terms || 14;
    const now = new Date();
    const issueDate = format(now, 'yyyy-MM-dd');
    const dueDate = format(new Date(now.getTime() + payTerms * 86400000), 'yyyy-MM-dd');

    setDraft({
      project: project ? { id: project.id, name: project.name, hourly_rate: project.hourly_rate, currency: project.currency } : null,
      customer: { id: customer.id, name: customer.name, vat_number: customer.vat_number, payment_terms: payTerms },
      exchange_rate: null,
    });
    // Prefill the buyer override fields with the chosen customer's data so
    // they're easy to inspect/tweak (and so the EU VAT ends up in the
    // visible "NIP / Tax ID" slot when the customer has no Polish NIP).
    const customerTaxOrVat = customer?.tax_id || customer?.vat_number || '';
    setInvoiceData(prev => ({
      ...prev,
      currency: project?.currency || 'EUR',
      issue_date: issueDate,
      sale_date: issueDate,
      due_date: dueDate,
      line_items: [],
      buyer_name: prev.buyer_name || customer?.name || '',
      buyer_address: prev.buyer_address || customer?.invoice_address || customer?.address || '',
      buyer_tax_id: prev.buyer_tax_id || customerTaxOrVat,
      buyer_vat_number: prev.buyer_vat_number || customer?.vat_number || '',
      buyer_country_code: prev.buyer_country_code || (customer?.country || 'PL').slice(0, 2).toUpperCase(),
    }));
    setStep(2);
  };

  const addLineItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: crypto.randomUUID(),
        description: '',
        quantity: 1,
        unit: 'service',
        unit_price: 0,
        net_amount: 0,
        vat_amount: 0,
        gross_amount: 0,
        source_type: 'manual',
      }],
    }));
  };

  const addFromSaved = (saved) => {
    const qty = saved.default_quantity || 1;
    const price = saved.default_unit_price || 0;
    setInvoiceData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: crypto.randomUUID(),
        description: saved.description,
        quantity: qty,
        unit: saved.unit || 'service',
        unit_price: price,
        net_amount: qty * price,
        vat_amount: 0,
        gross_amount: qty * price,
        source_type: 'saved_item',
      }],
    }));
    setShowSavedPicker(false);
  };

  const removeLineItem = (index) => {
    setInvoiceData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const updateLineItem = (index, field, value) => {
    setInvoiceData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      // recalc
      const qty = items[index].quantity || 0;
      const price = items[index].unit_price || 0;
      items[index].net_amount = qty * price;
      items[index].gross_amount = items[index].net_amount;
      return { ...prev, line_items: items };
    });
  };

  const saveItemForReuse = async (item) => {
    if (!item.description) {
      toast.error(t('invoices.toasts.itemDescriptionRequired'));
      return;
    }
    try {
      await axios.post(`${API}/api/settings/saved-invoice-items`, {
        name: item.description.substring(0, 50),
        description: item.description,
        unit: item.unit || 'service',
        default_quantity: item.quantity || 1,
        default_unit_price: item.unit_price || null,
        default_currency: invoiceData.currency,
      });
      toast.success(t('invoices.toasts.itemSaved'));
      // Refresh saved items list
      axios.get(`${API}/api/settings/saved-invoice-items`).then(r => setSavedItems(r.data)).catch(() => {});
    } catch {
      toast.error(t('invoices.toasts.itemSaveError'));
    }
  };

  const totalNet = invoiceData.line_items.reduce((s, i) => s + (i.net_amount || 0), 0);

  // Per-project subtotals — read-only mirror of the PDF grouping so the
  // user can sanity-check each site's cost before saving.
  const projectTotals = React.useMemo(() => {
    const map = new Map();
    invoiceData.line_items.forEach(li => {
      if (!li.project_id) return;
      const key = li.project_id;
      const prev = map.get(key) || {
        name: li.project_name || projects.find(p => p.id === key)?.name || key,
        hours: 0, net: 0,
      };
      prev.net += li.net_amount || 0;
      if (li.unit === 'hour') prev.hours += Number(li.quantity) || 0;
      map.set(key, prev);
    });
    return Array.from(map.values());
  }, [invoiceData.line_items, projects]);

  const handleSave = async () => {
    if (invoiceData.line_items.length === 0) {
      toast.error(t('invoices.toasts.needAtLeastOneItem'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomer || draft?.customer?.id,
        project_id: selectedProject || draft?.project?.id || null,
        // Full list when several projects are billed on one invoice.
        project_ids: (
          selectedProjects.length
            ? selectedProjects
            : [selectedProject || draft?.project?.id].filter(Boolean)
        ),
        issue_date: new Date(invoiceData.issue_date).toISOString(),
        sale_date: new Date(invoiceData.sale_date).toISOString(),
        due_date: new Date(invoiceData.due_date).toISOString(),
        currency: invoiceData.currency,
        exchange_rate: draft?.exchange_rate?.rate || null,
        exchange_rate_date: draft?.exchange_rate?.date || null,
        exchange_rate_table: draft?.exchange_rate?.table || null,
        reverse_charge: invoiceData.reverse_charge,
        vat_rate: invoiceData.vat_rate,
        reverse_charge_text: invoiceData.reverse_charge_text,
        notes: invoiceData.notes,
        supplier_number: invoiceData.supplier_number,
        location: invoiceData.location,
        contact_info: invoiceData.contact_info,
        line_items: invoiceData.line_items,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        bank_account: invoiceData.bank_account || (companyBanks[invoiceData.currency]?.iban) || null,
        issued_by_name: invoiceData.issued_by_name || null,
        invoice_number: invoiceData.invoice_number || null,
        // Phase 2 overrides — only send when actually edited (avoid empty
        // strings overriding clean defaults)
        seller_name: invoiceData.seller_name || null,
        seller_address: invoiceData.seller_address || null,
        seller_tax_id: invoiceData.seller_tax_id || null,
        seller_country_code: invoiceData.seller_country_code || null,
        buyer_name: invoiceData.buyer_name || null,
        buyer_address: invoiceData.buyer_address || null,
        buyer_tax_id: invoiceData.buyer_tax_id || null,
        buyer_vat_number: invoiceData.buyer_vat_number || null,
        buyer_country_code: invoiceData.buyer_country_code || null,
        // ---- Payment method + already-paid flag (Phase 5) ----
        payment_method: invoiceData.payment_method || 'bank_transfer',
        is_paid: !!invoiceData.is_paid,
        paid_date: invoiceData.is_paid && invoiceData.paid_date
          ? new Date(invoiceData.paid_date).toISOString()
          : null,
      };

      if (editInvoice) {
        await axios.put(`${API}/api/invoices/${editInvoice.id}`, payload);
        toast.success(t('invoices.toasts.updated'));
      } else {
        await axios.post(`${API}/api/invoices`, payload);
        toast.success(t('invoices.toasts.created'));
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.toasts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle>
            {editInvoice
              ? (editInvoice.is_correction ? `${t('invoiceDlg.editCorrectionKor')} — ${editInvoice.invoice_number || ''}` : t('invoices.editInvoice'))
              : t('invoices.createInvoice')}
          </DialogTitle>
          {editInvoice?.is_correction && (
            <DialogDescription className="text-blue-800 bg-blue-50 px-2 py-1.5 rounded mt-2 text-xs">
              <strong>{t('invoiceDlg.fakturaKorygujCa')}</strong> — {t('invoiceDlg.correctsInvoice')}
              <span className="font-mono mx-1">{editInvoice.corrects_invoice_number}</span>
              {editInvoice.corrects_ksef_number && (
                <>{t('auto.misc2.ksefRef')} <span className="font-mono">{editInvoice.corrects_ksef_number}</span>)</>
              )}
              . {t('invoiceDlg.correctionHint')}
            </DialogDescription>
          )}
          <DialogDescription>
            {step === 1 ? t('invoiceDlg.chooseHowToCreate') : t('invoiceDlg.reviewAndCustomize')}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-5">
            {/* Mode Selector */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                className={`p-4 border-2 rounded-lg text-left transition-colors ${mode === 'timesheet' ? 'border-[#AA0000] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setMode('timesheet')}
                data-testid="mode-timesheet"
              >
                <Clock className="h-5 w-5 mb-2 text-[#AA0000]" />
                <div className="font-medium">{t('invoiceDlg.fromTimesheets')}</div>
                <div className="text-sm text-slate-500">{t('invoiceDlg.generateFromProjectHours')}</div>
              </button>
              <button
                className={`p-4 border-2 rounded-lg text-left transition-colors ${mode === 'manual' ? 'border-[#AA0000] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setMode('manual')}
                data-testid="mode-manual"
              >
                <PenLine className="h-5 w-5 mb-2 text-[#AA0000]" />
                <div className="font-medium">{t('invoiceDlg.manualInvoice')}</div>
                <div className="text-sm text-slate-500">{t('invoiceDlg.enterItemsManually')}</div>
              </button>
            </div>

            {mode === 'timesheet' ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>{t('invoices.create.projectsLabel')}</Label>
                    <span className="text-xs text-slate-500" data-testid="selected-projects-count">
                      {t('invoices.create.projectsSelected', { count: selectedProjects.length })}
                    </span>
                  </div>
                  <div className="max-h-[220px] overflow-y-auto rounded-lg border border-slate-200 divide-y"
                       data-testid="project-multiselect">
                    {projects.map(p => {
                      const checked = selectedProjects.includes(p.id);
                      const lockedCustomer = selectedProjects.length > 0
                        && projects.find(x => x.id === selectedProjects[0])?.customer_id;
                      const otherCustomer = !!lockedCustomer && p.customer_id !== lockedCustomer;
                      return (
                        <label key={p.id}
                          className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${checked ? 'bg-red-50' : 'hover:bg-slate-50'} ${otherCustomer ? 'opacity-40' : ''}`}
                          data-testid={`project-option-${p.id}`}>
                          <input type="checkbox" className="accent-[#AA0000]"
                            checked={checked}
                            disabled={otherCustomer}
                            onChange={() => toggleProject(p.id)}
                            data-testid={`project-checkbox-${p.id}`} />
                          <span className="flex-1">
                            {p.name}
                            {p.status !== 'active' && p.status !== 'Active' ? ` (${p.status})` : ''}
                          </span>
                          <span className="text-xs text-slate-400">
                            {customers.find(c => c.id === p.customer_id)?.name || ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t('invoices.create.projectsHint')}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('invoiceDlg.periodStart')}</Label>
                    <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="period-start" />
                  </div>
                  <div>
                    <Label>{t('invoiceDlg.periodEnd')}</Label>
                    <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="period-end" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>{t('invoiceDlg.customer')}</Label>
                  <Select value={selectedCustomer} onValueChange={(v) => { setSelectedCustomer(v); setSelectedProject(''); }}>
                    <SelectTrigger data-testid="select-customer">
                      <SelectValue placeholder={t('invoiceDlg.selectACustomer')} />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('invoiceDlg.projectOptional')}</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger data-testid="select-project-manual">
                      <SelectValue placeholder={t('invoiceDlg.selectAProjectOptional')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('invoiceDlg.noProject')}</SelectItem>
                      {projects.filter(p => p.customer_id === selectedCustomer).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
              {mode === 'timesheet' ? (
                <Button onClick={handleLoadDraft} disabled={loading} data-testid="load-draft-btn" className="bg-[#AA0000] hover:bg-[#880000]">
                  {loading ? t('common.loading') : t('invoiceDlg.loadDraft')}
                </Button>
              ) : (
                <Button onClick={handleStartManual} data-testid="start-manual-btn" className="bg-[#AA0000] hover:bg-[#880000]">
                  {t('invoiceDlg.continue')}
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Header */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <h4 className="font-medium text-slate-700">{t('invoiceDlg.customer')}</h4>
                <p className="text-slate-600">{draft?.customer?.name || customers.find(c => c.id === selectedCustomer)?.name}</p>
                {draft?.customer?.vat_number && <p className="text-sm text-slate-500">VAT: {draft.customer.vat_number}</p>}
              </div>
              {(draft?.projects?.length > 1) ? (
                <div>
                  <h4 className="font-medium text-slate-700">{t('invoices.create.projectsLabel')}</h4>
                  <ul className="text-slate-600 text-sm space-y-0.5" data-testid="draft-projects-list">
                    {draft.projects.map(p => (
                      <li key={p.id}>
                        {p.name}
                        {p.hourly_rate > 0 && (
                          <span className="text-slate-500"> — {p.hourly_rate} {p.currency || invoiceData.currency}/hr</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : draft?.project && (
                <div>
                  <h4 className="font-medium text-slate-700">{t('invoiceDlg.project')}</h4>
                  <p className="text-slate-600">{draft.project.name}</p>
                  {draft.project.hourly_rate > 0 && (
                    <p className="text-sm text-slate-500">Rate: {draft.project.hourly_rate} {draft.project.currency || invoiceData.currency}/hr</p>
                  )}
                </div>
              )}
            </div>

            {/* Party details (Phase 2 — overridable seller AND buyer) */}
            <div className="rounded-lg border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setShowPartyOverride(s => !s)}
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="toggle-party-override"
              >
                <span>{t('invoiceDlg.sellerBuyerDetails')} {showPartyOverride ? t('invoiceDlg.editing') : (
                  (invoiceData.seller_name || invoiceData.buyer_name || invoiceData.buyer_tax_id || invoiceData.buyer_vat_number)
                    ? t('invoiceDlg.customClickEdit')
                    : t('invoiceDlg.usingDefaults')
                )}</span>
                <span className="text-xs text-slate-400">{showPartyOverride ? '▲' : '▼'}</span>
              </button>
              {showPartyOverride && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border-t border-slate-200">
                  {/* Seller */}
                  <div className="space-y-2">
                    <h5 className="text-xs font-semibold uppercase text-slate-500">{t('invoiceDlg.sellerSprzedawca')}</h5>
                    <div>
                      <Label className="text-xs">{t('common.name')}</Label>
                      <Input value={invoiceData.seller_name}
                        placeholder={companyProfile?.name || 'Expertfix Sp. z o.o.'}
                        onChange={e => setInvoiceData(p => ({ ...p, seller_name: e.target.value }))}
                        data-testid="seller-name" />
                    </div>
                    <div>
                      <Label className="text-xs">{t('invoiceDlg.address')}</Label>
                      <Input value={invoiceData.seller_address}
                        placeholder={companyProfile ? `${companyProfile.address || ''}, ${companyProfile.postal_code || ''} ${companyProfile.city || ''}`.trim() : ''}
                        onChange={e => setInvoiceData(p => ({ ...p, seller_address: e.target.value }))}
                        data-testid="seller-address" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-2">
                        <Label className="text-xs">{t('invoiceDlg.taxIdNip')}</Label>
                        <Input value={invoiceData.seller_tax_id}
                          placeholder={companyProfile?.tax_id || ''}
                          onChange={e => setInvoiceData(p => ({ ...p, seller_tax_id: e.target.value }))}
                          data-testid="seller-tax-id" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('invoiceDlg.country')}</Label>
                        <Input value={invoiceData.seller_country_code} maxLength={2}
                          placeholder={(companyProfile?.country || 'PL').slice(0, 2).toUpperCase()}
                          onChange={e => setInvoiceData(p => ({ ...p, seller_country_code: e.target.value.toUpperCase() }))}
                          data-testid="seller-country-code" />
                      </div>
                    </div>
                  </div>

                  {/* Buyer */}
                  <div className="space-y-2">
                    <h5 className="text-xs font-semibold uppercase text-slate-500">{t('invoiceDlg.buyerNabywca')}</h5>
                    <div>
                      <Label className="text-xs">{t('common.name')}</Label>
                      <Input value={invoiceData.buyer_name}
                        placeholder={draft?.customer?.name || customers.find(c => c.id === selectedCustomer)?.name || ''}
                        onChange={e => setInvoiceData(p => ({ ...p, buyer_name: e.target.value }))}
                        data-testid="buyer-name" />
                    </div>
                    <div>
                      <Label className="text-xs">{t('invoiceDlg.address')}</Label>
                      <Input value={invoiceData.buyer_address}
                        placeholder={(customers.find(c => c.id === selectedCustomer)?.invoice_address) || (customers.find(c => c.id === selectedCustomer)?.address) || ''}
                        onChange={e => setInvoiceData(p => ({ ...p, buyer_address: e.target.value }))}
                        data-testid="buyer-address" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">{t('invoiceDlg.taxIdNip')}</Label>
                        <Input value={invoiceData.buyer_tax_id}
                          placeholder={t('invoiceDlg.eG5260250274OrDk61628711')}
                          onChange={e => setInvoiceData(p => ({ ...p, buyer_tax_id: e.target.value }))}
                          data-testid="buyer-tax-id" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('auto.misc2.euVat')}</Label>
                        <Input value={invoiceData.buyer_vat_number}
                          placeholder={t('auto.misc2.dk61628711')}
                          onChange={e => setInvoiceData(p => ({ ...p, buyer_vat_number: e.target.value }))}
                          data-testid="buyer-vat-number" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{t('invoiceDlg.country')}</Label>
                      <Input value={invoiceData.buyer_country_code} maxLength={2}
                        placeholder={(customers.find(c => c.id === selectedCustomer)?.country || 'PL').slice(0, 2).toUpperCase()}
                        onChange={e => setInvoiceData(p => ({ ...p, buyer_country_code: e.target.value.toUpperCase() }))}
                        data-testid="buyer-country-code" />
                    </div>
                  </div>

                  <div className="sm:col-span-2 text-xs text-slate-500 italic">
                    {t('invoiceDlg.leaveEmptyDefaults')}
                    The Buyer Tax ID is the field that ends up on the printed and KSeF-uploaded invoice.
                    For non-Polish customers without a NIP, paste the full EU VAT here (e.g. <code>{t('auto.misc2.dk61628711')}</code>).
                  </div>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>{t('invoiceDlg.issueDate')}</Label>
                <Input type="date" value={invoiceData.issue_date}
                  onChange={e => setInvoiceData(prev => ({ ...prev, issue_date: e.target.value }))}
                  data-testid="issue-date" />
              </div>
              <div>
                <Label>{t('invoiceDlg.saleDate')}</Label>
                <Input type="date" value={invoiceData.sale_date}
                  onChange={e => setInvoiceData(prev => ({ ...prev, sale_date: e.target.value }))}
                  data-testid="sale-date" />
              </div>
              <div>
                <Label>{t('invoiceDlg.dueDate')}</Label>
                <Input type="date" value={invoiceData.due_date}
                  onChange={e => setInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="due-date" />
              </div>
            </div>

            {/* Payment method + already-paid (Phase 5) — both the
                invoice PDF and the KSeF FA(3) XML read from these. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label>{t('invoiceDlg.paymentMethod')}</Label>
                <Select
                  value={invoiceData.payment_method}
                  onValueChange={v => setInvoiceData(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger data-testid="invoice-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">{t('invoiceDlg.przelewBankTransfer')}</SelectItem>
                    <SelectItem value="cash">{t('invoiceDlg.gotWkaCash')}</SelectItem>
                    <SelectItem value="card">{t('invoiceDlg.kartaCard')}</SelectItem>
                    <SelectItem value="voucher">{t('invoiceDlg.bonVoucher')}</SelectItem>
                    <SelectItem value="cheque">{t('invoiceDlg.czekCheque')}</SelectItem>
                    <SelectItem value="letter_of_credit">{t('invoiceDlg.akredytywaLetterOfCredit')}</SelectItem>
                    <SelectItem value="other">{t('invoiceDlg.inneOther')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-slate-600">{t('invoiceDlg.paidAtIssue')}</Label>
                <label className="flex items-center gap-2 h-9 pl-2 border rounded-md bg-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!invoiceData.is_paid}
                    onChange={e => setInvoiceData(prev => ({
                      ...prev,
                      is_paid: e.target.checked,
                      // Default paid_date to issue_date when the box
                      // flips on — user can still override.
                      paid_date: e.target.checked
                        ? (prev.paid_date || prev.issue_date || '')
                        : '',
                    }))}
                    data-testid="invoice-is-paid-checkbox"
                  />
                  <span className="text-sm">{t('invoiceDlg.alreadyPaid')}</span>
                </label>
              </div>
              {invoiceData.is_paid && (
                <div>
                  <Label>{t('invoiceDlg.paymentDate')}</Label>
                  <Input
                    type="date"
                    value={invoiceData.paid_date || ''}
                    onChange={e => setInvoiceData(prev => ({ ...prev, paid_date: e.target.value }))}
                    data-testid="invoice-paid-date"
                  />
                </div>
              )}
            </div>

            {/* Exchange Rate Info */}
            {draft?.exchange_rate && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <strong>{t('invoiceDlg.nbpExchangeRate')}</strong> 1 {draft.exchange_rate.currency} = {draft.exchange_rate.rate} PLN
                <br />
                <span className="text-slate-600">{draft.exchange_rate.table} (date: {draft.exchange_rate.date})</span>
              </div>
            )}

            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-slate-700">{t('invoiceDlg.lineItems')}</h4>
                <div className="flex gap-2">
                  {savedItems.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => setShowSavedPicker(!showSavedPicker)} data-testid="from-saved-btn">
                      <BookMarked className="h-3 w-3 mr-1" /> {t('invoiceDlg.fromSaved')}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                    <Plus className="h-3 w-3 mr-1" /> {t('invoiceDlg.addItem')}
                  </Button>
                </div>
              </div>
              {showSavedPicker && (
                <div className="mb-3 p-3 border rounded-lg bg-slate-50 space-y-2" data-testid="saved-items-picker">
                  <div className="text-sm font-medium text-slate-600">{t('invoiceDlg.selectASavedItem')}</div>
                  <div className="grid gap-1 max-h-[160px] overflow-y-auto">
                    {savedItems.map(item => (
                      <button key={item.id}
                        className="flex justify-between items-center p-2 text-left text-sm rounded hover:bg-white border border-transparent hover:border-slate-200 transition-colors"
                        onClick={() => addFromSaved(item)}
                        data-testid={`pick-saved-item-${item.id}`}>
                        <div>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-slate-500">{item.description}</div>
                        </div>
                        <div className="text-xs text-slate-400 whitespace-nowrap ml-2">
                          {item.default_unit_price ? `${item.default_unit_price} ${item.default_currency}` : '-'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">{t('invoices.columns.description')}</TableHead>
                    <TableHead className="w-[10%]">{t('invoices.columns.unit')}</TableHead>
                    <TableHead className="w-[12%] text-right">{t('invoices.columns.qty')}</TableHead>
                    <TableHead className="w-[15%] text-right">{t('invoices.columns.unitPrice')}</TableHead>
                    <TableHead className="w-[15%] text-right">{t('invoices.columns.netAmount')}</TableHead>
                    <TableHead className="w-[8%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceData.line_items.map((item, idx) => (
                    <TableRow key={item.id || idx}>
                      <TableCell>
                        <Input value={item.description}
                          onChange={e => updateLineItem(idx, 'description', e.target.value)}
                          placeholder={t('invoiceDlg.serviceDescription')}
                          data-testid={`line-item-desc-${idx}`} />
                        {item._hours_breakdown && (
                          <div className="text-xs text-slate-500 mt-1">
                            {item._hours_breakdown.map(h => `${h.name}: ${h.hours}h`).join(', ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select value={item.unit || 'service'} onValueChange={v => updateLineItem(idx, 'unit', v)}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="service">service</SelectItem>
                            <SelectItem value="hour">hour</SelectItem>
                            <SelectItem value="piece">piece</SelectItem>
                            <SelectItem value="item">item</SelectItem>
                            <SelectItem value="day">day</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.5" className="text-right h-8"
                          value={item.quantity}
                          onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          data-testid={`line-item-qty-${idx}`} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" className="text-right h-8"
                          value={item.unit_price}
                          onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          data-testid={`line-item-price-${idx}`} />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtCurrency(item.net_amount, invoiceData.currency)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={t('invoiceDlg.saveForReuse')}
                            onClick={() => saveItemForReuse(item)}
                            data-testid={`save-line-item-${idx}`}>
                            <BookMarked className="h-3 w-3 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLineItem(idx)}
                            data-testid={`remove-line-item-${idx}`}>
                            <X className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {invoiceData.line_items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-6">
                        {t('invoiceDlg.noLineItemsClickAddItem')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="mt-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                {projectTotals.length > 1 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm min-w-[260px]"
                       data-testid="per-project-totals">
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">
                      {t('invoices.create.perProjectTotals')}
                    </div>
                    {projectTotals.map(p => (
                      <div key={p.name} className="flex justify-between gap-4 py-0.5">
                        <span className="text-slate-600">
                          {p.name}
                          {p.hours ? <span className="text-slate-400"> · {p.hours} h</span> : null}
                        </span>
                        <span className="font-medium">{fmtCurrency(p.net, invoiceData.currency)}</span>
                      </div>
                    ))}
                  </div>
                ) : <div />}
                <div className="text-right space-y-1">
                  <div className="text-lg font-bold" data-testid="invoice-total">
                    {t('invoiceDlg.total')} {fmtCurrency(totalNet, invoiceData.currency)}
                  </div>
                  {draft?.exchange_rate && (
                    <div className="text-sm text-slate-500">
                      ≈ {fmtCurrency(totalNet * draft.exchange_rate.rate, 'PLN')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* VAT Settings */}
            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox id="reverse_charge" checked={invoiceData.reverse_charge}
                  onCheckedChange={checked => setInvoiceData(prev => ({ ...prev, reverse_charge: checked }))}
                  data-testid="reverse-charge-checkbox" />
                <Label htmlFor="reverse_charge">{t('invoiceDlg.reverseChargeVatPaidByBuyer')}</Label>
              </div>
              {!invoiceData.reverse_charge && (
                <div className="ml-6">
                  <Label>{t('invoiceDlg.vatRate')}</Label>
                  <Input type="number" className="w-24" value={invoiceData.vat_rate}
                    onChange={e => setInvoiceData(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
                    data-testid="vat-rate-input" />
                </div>
              )}
              {invoiceData.reverse_charge && (
                <div className="mt-2">
                  <Label>{t('invoiceDlg.reverseChargeTextBilingual')}</Label>
                  <Textarea value={invoiceData.reverse_charge_text}
                    onChange={e => setInvoiceData(prev => ({ ...prev, reverse_charge_text: e.target.value }))}
                    rows={3} placeholder={t('invoiceDlg.reverseChargeExplanationText')}
                    data-testid="reverse-charge-text" />
                </div>
              )}
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('invoiceDlg.currency')}</Label>
                <Select value={invoiceData.currency} onValueChange={v => {
                  const bankInfo = companyBanks[v] || {};
                  setInvoiceData(prev => ({ ...prev, currency: v, bank_account: bankInfo.iban || prev.bank_account }));
                }}>
                  <SelectTrigger data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['EUR', 'PLN', 'GBP', 'USD', 'DKK', 'SEK', 'NOK', 'CHF', 'CZK'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('invoiceDlg.supplierNumber')}</Label>
                <Input value={invoiceData.supplier_number}
                  onChange={e => setInvoiceData(prev => ({ ...prev, supplier_number: e.target.value }))}
                  placeholder={t('invoiceDlg.customerSSupplierNumber')} data-testid="supplier-number" />
              </div>
            </div>
            {/* Bank account for selected currency */}
            {(() => {
              const bankInfo = companyBanks[invoiceData.currency];
              return bankInfo?.iban ? (
                <div className="bg-slate-50 rounded-md p-3 text-sm space-y-1">
                  <span className="font-medium text-slate-700">{t('invoiceDlg.bankAccount')} ({invoiceData.currency}):</span>
                  <span className="ml-2">{bankInfo.iban}</span>
                  {bankInfo.swift && <span className="ml-2 text-slate-500">| SWIFT: {bankInfo.swift}</span>}
                  {bankInfo.bank_name && <span className="ml-2 text-slate-500">| {bankInfo.bank_name}</span>}
                </div>
              ) : null;
            })()}
            <div>
              <Label>{t('invoiceDlg.locationReference')}</Label>
              <Input value={invoiceData.location}
                onChange={e => setInvoiceData(prev => ({ ...prev, location: e.target.value }))}
                placeholder={t('invoiceDlg.eGAldiDcBartonUk')} data-testid="location-ref" />
            </div>
            <div>
              <Label>{t('invoiceDlg.contactInfo')}</Label>
              <Textarea value={invoiceData.contact_info}
                onChange={e => setInvoiceData(prev => ({ ...prev, contact_info: e.target.value }))}
                rows={2} placeholder={t('invoiceDlg.contactPersonsForThisInvoice')} data-testid="contact-info" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('invoiceDlg.invoiceNumber')}</Label>
                <Input value={invoiceData.invoice_number}
                  onChange={e => setInvoiceData(prev => ({ ...prev, invoice_number: e.target.value }))}
                  placeholder={editInvoice ? editInvoice.invoice_number : t('invoiceDlg.autoGeneratedIfEmpty')}
                  data-testid="invoice-number-input" />
                <p className="text-xs text-slate-500 mt-1">{t('invoiceDlg.leaveEmptyForAutoNumbering')}</p>
              </div>
              <div>
                <Label>{t('invoiceDlg.issuedByNameOnInvoice')}</Label>
                <Input value={invoiceData.issued_by_name}
                  onChange={e => setInvoiceData(prev => ({ ...prev, issued_by_name: e.target.value }))}
                  placeholder={t('invoiceDlg.personAuthorisedToIssue')}
                  data-testid="issued-by-name-input" />
              </div>
            </div>
            <div>
              <Label>{t('common.notes')}</Label>
              <RichTextNotes value={invoiceData.notes}
                onChange={val => setInvoiceData(prev => ({ ...prev, notes: val }))}
                placeholder={t('invoiceDlg.additionalNotesToAppearOnInvoice')} />
            </div>

            <DialogFooter>
              {!editInvoice && (
                <Button variant="outline" onClick={() => setStep(1)} data-testid="dialog-back-btn">{t('common.back')}</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-invoice-btn">
                {saving ? t('expenses.actions.saving') : editInvoice ? t('invoiceDlg.updateInvoice') : t('invoices.createInvoice')}
              </Button>
            </DialogFooter>
            <FloatingActions actions={[{
              icon: Save,
              label: saving ? t('invoiceDlg.saving') : (editInvoice ? t('invoiceDlg.updateInvoice') : t('invoiceDlg.createInvoice')),
              tone: 'primary', testId: 'invoice-floating-save-btn',
              disabled: saving, onClick: handleSave,
            }]} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== View Invoice Dialog ====================
const ViewInvoiceDialog = ({ open, onOpenChange, invoice, getCustomerName, getProjectName }) => {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Invoice {invoice.invoice_number}</DialogTitle>
          <DialogDescription>
            {t('invoiceDlg.invoiceDetails')}
          </DialogDescription>
          <Badge className={statusColors[invoice.status]}>{statusLabel(invoice.status)}</Badge>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <div className="text-sm text-slate-500">{t('invoiceDlg.customer')}</div>
              <div className="font-medium">{getCustomerName(invoice.customer_id)}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">{t('invoiceDlg.project')}</div>
              <div className="font-medium">{getProjectName(invoice.project_id)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-slate-500">{t('invoiceDlg.issueDate')}</div>
              <div className="font-medium">{fmtDate(invoice.issue_date)}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">{t('invoiceDlg.saleDate')}</div>
              <div className="font-medium">{fmtDate(invoice.sale_date)}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">{t('invoiceDlg.dueDate')}</div>
              <div className="font-medium">{fmtDate(invoice.due_date)}</div>
            </div>
          </div>

          {invoice.exchange_rate && (
            <div className="p-3 bg-blue-50 rounded text-sm">
              <strong>{t('invoiceDlg.exchangeRate')}</strong> 1 {invoice.currency} = {invoice.exchange_rate} PLN
              {invoice.exchange_rate_table && <span className="ml-2 text-slate-600">({invoice.exchange_rate_table})</span>}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>{t('invoiceDlg.description')}</TableHead>
                <TableHead className="text-right">{t('invoiceDlg.qty')}</TableHead>
                <TableHead>{t('invoiceDlg.unit')}</TableHead>
                <TableHead className="text-right">{t('invoiceDlg.unitPrice')}</TableHead>
                <TableHead className="text-center">{t('auto.misc2.vat')}</TableHead>
                <TableHead className="text-right">{t('invoiceDlg.netAmount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(invoice.line_items || []).map((item, i) => (
                <TableRow key={item.id || i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell className="text-right">{fmtCurrency(item.unit_price, invoice.currency)}</TableCell>
                  <TableCell className="text-center">{invoice.reverse_charge ? 'n/a' : `${item.vat_rate || 0}%`}</TableCell>
                  <TableCell className="text-right font-medium">{fmtCurrency(item.net_amount, invoice.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="text-right space-y-1 border-t pt-3">
            <div>{t('invoiceDlg.totalNet')}<strong>{fmtCurrency(invoice.total_net, invoice.currency)}</strong></div>
            {!invoice.reverse_charge && <div>{t('invoiceDlg.totalVat')}<strong>{fmtCurrency(invoice.total_vat, invoice.currency)}</strong></div>}
            <div className="text-lg">{t('invoiceDlg.totalGross')}<strong className="text-[#AA0000]">{fmtCurrency(invoice.total_gross, invoice.currency)}</strong></div>
            {invoice.total_gross_pln && invoice.currency !== 'PLN' && (
              <div className="text-sm text-slate-500">≈ {fmtCurrency(invoice.total_gross_pln, 'PLN')}</div>
            )}
            <div className="text-sm">{t('invoiceDlg.paidLabel')} {fmtCurrency(invoice.amount_paid, invoice.currency)}</div>
            <div className="text-sm font-medium">{t('invoiceDlg.remaining')} {fmtCurrency(invoice.amount_remaining, invoice.currency)}</div>
          </div>

          {invoice.reverse_charge && invoice.reverse_charge_text && (
            <div className="p-3 bg-gray-50 border-l-3 border-[#AA0000] text-sm whitespace-pre-line">
              {invoice.reverse_charge_text}
            </div>
          )}

          {(invoice.supplier_number || invoice.location || invoice.notes) && (
            <div className="space-y-1 text-sm">
              {invoice.supplier_number && <div><strong>{t('invoiceDlg.supplierNumber2')}</strong> {invoice.supplier_number}</div>}
              {invoice.location && <div><strong>{t('invoiceDlg.location')}</strong> {invoice.location}</div>}
              {invoice.contact_info && <div className="whitespace-pre-line"><strong>{t('invoiceDlg.contact')}</strong> {invoice.contact_info}</div>}
              {invoice.notes && <div><strong>{t('invoiceDlg.notes')}</strong> <div dangerouslySetInnerHTML={{ __html: invoice.notes }} /></div>}
            </div>
          )}

          {invoice.payments?.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">{t('invoiceDlg.payments')}</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('invoiceDlg.date')}</TableHead>
                    <TableHead>{t('invoiceDlg.method')}</TableHead>
                    <TableHead className="text-right">{t('invoiceDlg.amount')}</TableHead>
                    <TableHead>{t('invoiceDlg.reference')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.payments.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{fmtDate(p.payment_date)}</TableCell>
                      <TableCell>{p.payment_method}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(p.amount, p.currency)}</TableCell>
                      <TableCell>{p.reference || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PDF Download Dialog ====================
const PdfDownloadDialog = ({ open, onOpenChange, invoice }) => {
  const { t } = useTranslation();
  const [includeTimesheets, setIncludeTimesheets] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!invoice) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = includeTimesheets
        ? `${API}/api/invoices/${invoice.id}/pdf?include_timesheets=true`
        : `${API}/api/invoices/${invoice.id}/pdf`;

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const safeNumber = (invoice.invoice_number || '').replace(/\//g, '_');
      link.setAttribute('download', weekFileName(`invoice_${safeNumber}.pdf`));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      onOpenChange(false);
    } catch (error) {
      toast.error(t('invoiceDlg.failedToDownloadPdf'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pdf-download-dialog">
        <DialogHeader>
          <DialogTitle>{t('invoiceDlg.downloadInvoicePdf')}</DialogTitle>
          <DialogDescription>Invoice {invoice.invoice_number}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="include_timesheets" checked={includeTimesheets}
              onCheckedChange={setIncludeTimesheets}
              data-testid="include-timesheets-checkbox" />
            <Label htmlFor="include_timesheets">{t('invoiceDlg.includeAssociatedTimesheets')}</Label>
          </div>
          {includeTimesheets && (
            <p className="text-sm text-slate-500 ml-6">
              {t('invoiceDlg.timesheetDetailsForTheInvoicedPeriod')}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
          <Button onClick={handleDownload} disabled={downloading} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            {downloading ? t('invoiceDlg.generating') : t('invoiceDlg.downloadPdf')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Payment Dialog ====================
const PaymentDialog = ({ open, onOpenChange, invoice, onSuccess }) => {
  const { t } = useTranslation();
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'bank_transfer',
    reference: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setPaymentData(prev => ({
        ...prev,
        amount: invoice.amount_remaining || 0,
        currency: invoice.currency,
        payment_date: format(new Date(), 'yyyy-MM-dd'),
      }));
    }
  }, [open, invoice]);

  const handleSubmit = async () => {
    if (!invoice) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/invoices/${invoice.id}/payments`, {
        ...paymentData,
        payment_date: new Date(paymentData.payment_date).toISOString(),
      });
      toast.success(t('invoiceDlg.paymentRecorded'));
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(t('invoiceDlg.failedToRecordPayment'));
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>{t('invoiceDlg.recordPayment')}</DialogTitle>
          <DialogDescription>
            {t('invoices.title')} {invoice.invoice_number} — {t('invoiceDlg.remaining')} {fmtCurrency(invoice.amount_remaining, invoice.currency)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t('invoiceDlg.amount')} ({invoice.currency})</Label>
            <Input type="number" step="0.01" value={paymentData.amount}
              onChange={e => setPaymentData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              data-testid="payment-amount" />
          </div>
          <div>
            <Label>{t('invoiceDlg.paymentDate2')}</Label>
            <Input type="date" value={paymentData.payment_date}
              onChange={e => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
              data-testid="payment-date" />
          </div>
          <div>
            <Label>{t('invoiceDlg.paymentMethod2')}</Label>
            <Select value={paymentData.payment_method}
              onValueChange={v => setPaymentData(prev => ({ ...prev, payment_method: v }))}>
              <SelectTrigger data-testid="payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">{t('invoiceDlg.bankTransfer')}</SelectItem>
                <SelectItem value="cash">{t('invoiceDlg.cash')}</SelectItem>
                <SelectItem value="card">{t('invoiceDlg.card')}</SelectItem>
                <SelectItem value="other">{t('invoiceDlg.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('invoiceDlg.referenceTransactionId')}</Label>
            <Input value={paymentData.reference}
              onChange={e => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
              placeholder={t('invoiceDlg.bankReferenceNumber')} data-testid="payment-reference" />
          </div>
          <div>
            <Label>{t('common.notes')}</Label>
            <Textarea value={paymentData.notes}
              onChange={e => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={t('invoiceDlg.optionalNotes')} data-testid="payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="record-payment-btn">
            {saving ? t('invoiceDlg.recording') : t('invoiceDlg.recordPayment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicesTab;
